import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Balances,
  ClientTimeOffRequest,
  PayrollTimeOffRecord,
  PendingTimeOffRequest,
  TimeOffRecord,
  TimeOffRequest,
  TimeOffRequestList
} from '../models/TimeOffModels';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TimeOffRequestService {
  constructor(
    private http: HttpClient,
    @Inject('env') private env: any
  ) {}

  public acceptRequest(clientCode: string, personId: number, requestId: number) {
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests/${requestId}/Accept`,
      {}
    );
  }

  public cancelRequest(clientCode: string, personId: number, requestId: number) {
    return this.http.delete(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests/${requestId}`
    );
  }
  public cancelApprovedRequest(clientCode: string, personId: number, requestId: number) {
    return this.http.delete(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests/Approved/${requestId}`
    );
  }
  public getBalances(clientCode: string, personId: number) {
    return this.http.get<Balances[]>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests/Balances`
    );
  }

  public getRequests(clientCode: string, personId: number): Observable<TimeOffRequestList> {
    return this.http.get<TimeOffRequestList>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests`
    );
  }

  public submitRequest(clientCode: string, personId: number, model: TimeOffRequest) {
    return this.http.post<TimeOffRequest>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests`,
      model
    );
  }

  public updateRequest(
    clientCode: string,
    personId: number,
    requestId: number,
    model: TimeOffRequest
  ) {
    return this.http.put<TimeOffRequest>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests/${requestId}`,
      model
    );
  }

  public acceptEditedRequest(clientCode: string, personId: number, requestId: number) {
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests/Edited/${requestId}`,
      {}
    );
  }
  public cancelEditedRequest(clientCode: string, personId: number, requestId: number) {
    return this.http.delete(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests/Edited/${requestId}`
    );
  }

  public canEnable(clientCode: string, personId: number) {
    return this.http.get<{ Value: boolean }>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/TimeOffRequests/CanEnable`
    );
  }
}

@Injectable({
  providedIn: 'root'
})
export class ClientTimeOffRequestService {
  constructor(
    private http: HttpClient,
    @Inject('env') private env: any
  ) {}

  public createRequest(clientCode: string, request: ClientTimeOffRequest) {
    return this.http.post(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests`,
      request
    );
  }

  public getRequests(clientCode: string): Observable<TimeOffRequestList> {
    return this.http.get<TimeOffRequestList>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests`
    );
  }

  public getRequestsForPayroll(
    clientCode: string,
    personId: number,
    periodStart: Date,
    periodEnd: Date
  ) {
    return this.http.post<PayrollTimeOffRecord[]>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests/Approved/Payroll/${personId}`,
      {
        PeriodStart: periodStart,
        PeriodEnd: periodEnd
      }
    );
  }

  public approveRequest(clientCode: string, requestId: number, comment: string) {
    const body = { Comment: comment };
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests/${requestId}/Approve`,
      body
    );
  }
  public conditionallyApproveRequest(
    clientCode: string,
    requestId: number,
    request: TimeOffRequest
  ) {
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests/${requestId}`,
      request
    );
  }
  public declineRequest(clientCode: string, requestId: number, comment: string) {
    const body = { Comment: comment };
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests/${requestId}/Decline`,
      body
    );
  }
  public editApprovedRequest(clientCode: string, recordId: number, record: TimeOffRecord) {
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests/Approved/${recordId}`,
      record
    );
  }
  public cancelApprovedRequest(
    clientCode: string,
    personId: number,
    requestId: number,
    comment: string
  ) {
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests/Approved/${requestId}/delete`,
      { Comment: comment }
    );
  }
  public canEnable(clientCode: string) {
    return this.http.get<{ Value: boolean }>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests/CanEnable`
    );
  }

  public getCount(clientCode: string) {
    return this.http.get<{ Value: number }>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/TimeOffRequests/Count`
    );
  }
}
